import React, { useState } from 'react';
import { MessageCircle, Phone, X } from 'lucide-react';
import { Order, OrderItem } from '../../services/dataService';
import { resolveReceiptInvoiceNumber } from '../../services/saleSerials';

interface WhatsAppReceiptButtonProps {
  order: Order;
  cafeName?: string;
  cafeAddress?: string;
  cafePhone?: string;
  cafeInstagram?: string;
  onPhoneUpdate?: (orderId: string, phone: string) => Promise<void>;
  variant?: 'primary' | 'secondary' | 'icon';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const WhatsAppReceiptButton: React.FC<WhatsAppReceiptButtonProps> = ({
  order,
  cafeName = 'CafeLawn',
  cafeAddress = 'Your Cafe Address',
  cafePhone = 'Your Cafe Phone',
  cafeInstagram = '@cafelawn',
  onPhoneUpdate,
  variant = 'primary',
  size = 'md',
  className = ''
}) => {
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const formatReceiptMessage = () => {
    const orderDate = order.createdAt ? new Date(order.createdAt) : new Date();
    const formattedDate = orderDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    
    const invoiceNo = resolveReceiptInvoiceNumber(order);
    
    // Format items
    const itemsText = order.items?.map((item: OrderItem) => {
      const emoji = getItemEmoji(item.menuItemName);
      return `${emoji} ${item.menuItemName} (${item.quantity}) - Rs.${item.totalPrice.toFixed(2)}`;
    }).join('\n') || '';
    
    // Calculate tax (assuming 5% if not specified)
    const subtotal = order.subtotal || order.total;
    const tax = order.tax || (subtotal * 0.05);
    
    const message = `*Thank you for dining with us at ${cafeName}*

*Bill Receipt*
Date: ${formattedDate}
Invoice No: ${invoiceNo}

-------------------------------------
${itemsText}
-------------------------------------
*Subtotal:* Rs.${subtotal.toFixed(2)}
*Total:* Rs.${order.total.toFixed(2)}

*Business Details:*
${cafeName}
${cafeAddress}
Contact: ${cafePhone}

We hope you enjoyed your visit!
Follow us on Instagram: ${cafeInstagram}

---
Powered by CafeLawn.com`;

    return message;
  };

  const getItemEmoji = (itemName: string): string => {
    const name = itemName.toLowerCase();
    
    // Coffee items
    if (name.includes('coffee') || name.includes('espresso') || name.includes('americano') || name.includes('cappuccino') || name.includes('latte')) {
      return '[Coffee]';
    }
    // Tea items
    if (name.includes('tea') || name.includes('chai')) {
      return '[Tea]';
    }
    // Sandwiches
    if (name.includes('sandwich') || name.includes('burger') || name.includes('wrap')) {
      return '[Sandwich]';
    }
    // Pastries and desserts
    if (name.includes('cake') || name.includes('pastry') || name.includes('cookie') || name.includes('muffin') || name.includes('croissant')) {
      return '[Dessert]';
    }
    // Cold drinks
    if (name.includes('juice') || name.includes('smoothie') || name.includes('iced') || name.includes('cold')) {
      return '[Drink]';
    }
    // Salads
    if (name.includes('salad')) {
      return '[Salad]';
    }
    //cigarettes
    if (name.includes('cigarette')) {
      return '[Cigarette]';
    }
    // Default food emoji
    return '';
  };

  const handleWhatsAppClick = () => {
    if (!order.customerPhone) {
      alert('Customer phone number is not available for this order.');
      return;
    }

    // Clean phone number (remove spaces, dashes, etc.)
    const cleanPhone = order.customerPhone.replace(/\D/g, '');
    
    // Add country code if not present (assuming India +91)
    const phone = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`;
    
    const message = formatReceiptMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const handleAddPhoneNumber = () => {
    setShowPhoneModal(true);
    setPhoneNumber('');
  };

  const handlePhoneSubmit = async () => {
    if (!phoneNumber.trim()) {
      alert('Please enter a phone number');
      return;
    }

    // Validate phone number (basic validation)
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      alert('Please enter a valid phone number');
      return;
    }

    try {
      setIsUpdating(true);
      if (onPhoneUpdate && order.id) {
        await onPhoneUpdate(order.id, phoneNumber.trim());
        setShowPhoneModal(false);
        setPhoneNumber('');
      }
    } catch (error) {
      console.error('Error updating phone number:', error);
      alert('Failed to update phone number. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const getButtonClasses = (isGreen = false) => {
    const baseClasses = 'flex items-center justify-center transition-all duration-200';
    
    let variantClasses;
    if (isGreen) {
      variantClasses = {
        primary: 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium',
        secondary: 'bg-green-100 dark:bg-green-900 hover:bg-green-200 dark:hover:bg-green-800 text-green-700 dark:text-green-300 px-4 py-2 rounded-lg font-medium border border-green-300 dark:border-green-600',
        icon: 'bg-gray-100 dark:bg-gray-700 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 px-2 py-1 rounded border border-green-200 dark:border-green-600'
      };
    } else {
      variantClasses = {
        primary: 'bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium',
        secondary: 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg font-medium border border-gray-300 dark:border-gray-600',
        icon: 'bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-2 py-1 rounded border border-blue-200 dark:border-blue-600'
      };
    }

    const sizeClasses = {
      sm: 'text-xs px-3 py-2 rounded-lg',
      md: 'text-sm px-4 py-2 rounded-lg',
      lg: 'text-base px-5 py-3 rounded-lg'
    };
    
    return `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm': return 'w-3 h-3';
      case 'md': return 'w-4 h-4';
      case 'lg': return 'w-5 h-5';
      default: return 'w-4 h-4';
    }
  };

  const showText = variant !== 'icon';

  // Only show button if order is paid (remainingAmount <= 0)
  if (order.remainingAmount > 0) {
    return null;
  }

  return (
    <>
      {order.customerPhone ? (
        // Show "Send Receipt" button when phone exists
        <button
          onClick={handleWhatsAppClick}
          className={`w-full ${getButtonClasses(true)}`}
          title="Send receipt via WhatsApp"
        >
          <MessageCircle className={getIconSize()} />
          {showText && <span className="ml-2">Send Receipt</span>}
        </button>
      ) : (
        // Show "Add WhatsApp Number" button when no phone
        <button
          onClick={handleAddPhoneNumber}
          className={`w-full ${getButtonClasses()}`}
          title="Add WhatsApp number to send receipt"
        >
          <Phone className={getIconSize()} />
          {showText && <span className="ml-2">Add WhatsApp Number</span>}
        </button>
      )}

      {/* Phone Number Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add WhatsApp Number
                  </h3>
                  <p className="text-sm text-gray-600">
                    Customer: {order.customer}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowPhoneModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isUpdating}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter phone number (e.g., 9876543210)"
                  disabled={isUpdating}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the customer's WhatsApp number to send the receipt
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPhoneModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePhoneSubmit}
                  disabled={isUpdating || !phoneNumber.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? 'Adding...' : 'Add Number'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsAppReceiptButton; 